/**
 * Share to Microsoft Teams — official launcher.
 *
 * Opens Microsoft's own "Share to Teams" dialog (teams.microsoft.com/share)
 * in a popup. The signed-in user picks the chat or channel and sends — using
 * their own Teams session, on their own device.
 *
 * Why this design (SOC 2 + cost):
 *   - Nothing is stored and nothing is sent through PSI servers: the share
 *     happens entirely in the user's browser/Teams client. No tokens, no new
 *     consent grant, no new attack surface.
 *   - The shared payload is a *link back into the app* plus a short, non-
 *     sensitive note — never the underlying record body. The recipient must
 *     authenticate into the app to see any detail, so no PHI leaves our
 *     boundary via Teams.
 *   - Works identically whether the client's tenant is federated or not,
 *     because it rides the user's existing Teams login.
 *   - Zero additional Azure/Graph cost.
 *
 * Keep `message` short and free of sensitive detail (names, PHI, findings).
 * Put identifiers/titles only — the link carries the user to the real data.
 *
 * This file is kept identical across Core, Mandate, and Scrubber.
 */

const TEAMS_SHARE_BASE = 'https://teams.microsoft.com/share';

export function composeShareMessage({ appName, title, summary } = {}) {
  const app = (appName || 'Precision Compliance Group').trim();
  const headline = (summary || title || '').trim();
  return headline ? `${headline} — shared from ${app}.` : `Shared from ${app}.`;
}

export function buildTeamsShareUrl({ href, message } = {}) {
  const link = href || (typeof window !== 'undefined' ? window.location.href : '');
  const url = new URL(TEAMS_SHARE_BASE);
  url.searchParams.set('href', link);
  if (message) url.searchParams.set('msgText', message);
  url.searchParams.set('preview', 'true');
  return url.toString();
}

export function openTeamsShare({ href, message } = {}) {
  const shareUrl = buildTeamsShareUrl({ href, message });
  if (typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(shareUrl, 'pcg-teams-share', 'noopener,noreferrer,width=720,height=640');
  }
  return shareUrl;
}
